import {
  Appointment,
  AppointmentType,
  Hospitalisation,
  Patient,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import { getUserShortName } from "@/src/utils/utils";

import { CheckboxCard, CompartmentCard } from "@/src/components/cards";
import {
  closeDrawer,
  openConfirmDialog,
  openDrawer,
} from "@/src/components/dialogs";
import { FetchableList } from "@/src/components/fetchableLists";
import StaggeredAnimation from "@/src/components/staggeredAnimationComponent";
import { uid } from "@/src/utils/utils";
import moment from "moment";
import { useState } from "react";
import { openProtocolChoiceDrawer } from "../hospitalisation/cureHook";
import { openAppointment } from "../appointments/openAppointment";
import { DiagnosticsCompartment } from "../hospitalisation/diagnosticsCompartment";

function CuresHistory({
  title,
  types,
  patient,
  onSelect,
}: {
  title: string;
  types: AppointmentType[] | null | undefined;
  patient: Patient;
  onSelect?: (appointment: Appointment) => void;
}) {
  const [reload, setReload] = useState(0);

  function groupCuresByProtocol(
    data: Appointment[] | null | undefined
  ): Record<string, Appointment[]> {
    if (!data) return {};
    let group: Record<string, Appointment[]> = {};
    for (let appointment of data) {
      let protocolId = appointment?.protocol?.id;
      if (!protocolId) continue;
      group[protocolId] ||= [];
      group[protocolId].push(appointment);
    }
    return group;
  }

  function getProtocolName(
    data: Appointment[] | null | undefined,
    protocolId: string
  ) {
    for (let appointment of data || []) {
      if (appointment.protocol?.id != protocolId) continue;
      return appointment.protocol.name || "";
    }
    return "";
  }

  return (
    <CompartmentCard
      title={title}
      color="purple"
      onClick={async () => {
        await openProtocolChoiceDrawer({
          patient,
          onConfirm: async () => {
            setReload(reload + 1);
          },
        });
      }}
    >
      <FetchableList
        queryKey={["CuresList", types, patient.id, reload]}
        queryFn={() =>
          apiFetch("appointments", "loadMany", {
            patientId: patient.id,
            appointmentTypesIds: types?.map((type) => type.id || ""),
            hasProtocol: true,
          })
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {Object.entries(groupCuresByProtocol(data))?.map(
              ([protocolId, appointments]) => {
                return (
                  <div key={uid()} className="grid c1 gap10">
                    <div className="bold">
                      {getProtocolName(data, protocolId) || ""}
                    </div>
                    {appointments?.map((appointment, index) => {
                      let isPending =
                        appointment.status != "finished" &&
                        (appointment.item as Hospitalisation)?.status ==
                          "pending";

                      let bed = (appointment?.item as Hospitalisation)?.bed;

                      let C = appointment?.protocolCycle;
                      let J = appointment?.protocolDay?.day || 0;
                      return (
                        <StaggeredAnimation key={uid()} index={index}>
                          <CheckboxCard
                            color="purple"
                            status={
                              appointment.status == "finished"
                                ? "active"
                                : isPending
                                ? "pending"
                                : "inactive"
                            }
                            title={
                              <div>
                                <span className="black">
                                  C{C}{" "}
                                  <span className=" purple-color bold">
                                    J{J}
                                  </span>
                                </span>
                              </div>
                            }
                            subTitle={
                              <div className=" black bold">
                                {isPending ? "Hospitalisation en cours " : ""}
                                {bed?.name || ""}
                              </div>
                            }
                            onClick={async () => {
                              if (onSelect) return await onSelect(appointment);
                              await openAppointment(appointment, () => {
                                setReload(reload + 1);
                              });
                            }}
                            onDelete={async () => {
                              await openConfirmDialog({
                                onConfirm: async () => {
                                  await apiFetch("general", "deleteAny", {
                                    id: appointment.id,
                                    table: "appointments",
                                  });
                                  setReload(reload + 1);
                                },
                              });
                            }}
                          >
                            <div className="gray">
                              {getUserShortName(appointment?.creator)}
                            </div>
                            <div className="gray">
                              {moment(appointment.start).format("LL à HH:mm")}
                            </div>
                          </CheckboxCard>
                        </StaggeredAnimation>
                      );
                    })}{" "}
                  </div>
                );
              }
            )}
          </div>
        )}
      </FetchableList>
    </CompartmentCard>
  );
}

async function openCuresHistoryDrawer({
  patient,
  onSelect,
}: {
  patient: Patient;
  onSelect: (appointment: Appointment) => void;
}): Promise<void> {
  await openDrawer({
    id: "curesHistoryDrawer",
    title: "Historique des cures",
    content: (
      <div className="c1 gap15">
        <DiagnosticsCompartment patient={patient} parent={null} />
        <CuresHistory
          title={"Cures"}
          types={null}
          patient={patient}
          onSelect={async (appointment) => {
            closeDrawer("curesHistoryDrawer");
            await onSelect(appointment);
          }}
        />
      </div>
    ),
  });
}

export { CuresHistory, openCuresHistoryDrawer };
